import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import type { Conversation } from 'src/api-sdk';

import { useAssistant } from './AssistantProvider';
import { useConversations } from './useConversations';
import { PlusIcon } from './icons';

const whenOf = (c: Conversation) =>
  moment.utc(c.updated_at || c.created_at).valueOf();

/*
 * Conversation list view (replaces the transcript when the clock icon is
 * toggled). New-chat button on top; uppercase Today/Earlier group labels;
 * rows = title + relative date (absolute on hover). Active row is tinted navy.
 * Cursor-paginated infinite scroll.
 */
const ConversationList = () => {
  const { t } = useTranslation('assistant');
  const {
    conversationId,
    newChat,
    openConversation,
    conversationsRefreshKey,
    reportError,
  } = useAssistant();
  const query = useConversations();
  const { refetch } = query;

  useEffect(() => {
    if (query.error) reportError(query.error, 'list');
  }, [query.error, reportError]);

  // Refresh in place when a background update bumps the signal while the list
  // is open (skip the initial mount — the query fetches on mount already).
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    refetch();
  }, [conversationsRefreshKey, refetch]);

  // Latest conversation first (newest updated_at), then grouped by day.
  const conversations: Conversation[] = useMemo(
    () =>
      (query.data?.pages || [])
        .flatMap((p) => p.results || [])
        .sort((a, b) => whenOf(b) - whenOf(a)),
    [query.data]
  );

  const { today, earlier } = useMemo(() => {
    const startOfToday = moment().startOf('day');
    const t0: Conversation[] = [];
    const e0: Conversation[] = [];
    conversations.forEach((c) => {
      const when = moment.utc(c.updated_at || c.created_at).local();
      (when.isSameOrAfter(startOfToday) ? t0 : e0).push(c);
    });
    return { today: t0, earlier: e0 };
  }, [conversations]);

  const containerRef = useRef<HTMLDivElement>(null);

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      el.scrollTop + el.clientHeight >= el.scrollHeight - 80 &&
      query.hasNextPage &&
      !query.isFetchingNextPage
    ) {
      query.fetchNextPage();
    }
  };

  // When a page doesn't fill the container, onScroll never fires, so older
  // conversations would stay unreachable even though hasNextPage is true.
  // Keep loading until the list either scrolls or runs out of pages.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !query.hasNextPage || query.isFetchingNextPage) return;
    if (el.scrollHeight <= el.clientHeight) {
      query.fetchNextPage();
    }
  }, [conversations, query.hasNextPage, query.isFetchingNextPage]);

  const renderRow = (c: Conversation) => {
    const when = moment.utc(c.updated_at || c.created_at).local();
    // has_unread is new in the serializer (not yet in the generated SDK type).
    const hasUnread = Boolean(
      (c as unknown as { has_unread?: boolean }).has_unread
    );
    return (
      <button
        key={c.external_id}
        type="button"
        className={`bra-conv${
          c.external_id === conversationId ? ' bra-on' : ''
        }`}
        onClick={() => openConversation(c.external_id)}
      >
        <div className="bra-convrow">
          <div className="bra-t">{c.title || t('New conversation')}</div>
          {hasUnread ? <span className="bra-unread" /> : null}
        </div>
        <div className="bra-d" title={when.format('LLL')}>
          {when.fromNow()}
        </div>
      </button>
    );
  };

  return (
    <div className="bra-convs" onScroll={onScroll} ref={containerRef}>
      <button type="button" className="bra-newc" onClick={newChat}>
        <PlusIcon />
        {t('New chat')}
      </button>

      {today.length > 0 ? <div className="bra-cgroup">{t('Today')}</div> : null}
      {today.map(renderRow)}

      {earlier.length > 0 ? (
        <div className="bra-cgroup">{t('Earlier')}</div>
      ) : null}
      {earlier.map(renderRow)}

      {query.isFetching ? <div className="bra-convs-loading">…</div> : null}
    </div>
  );
};

export default ConversationList;
